import React, { useEffect, useMemo, useState } from "react";
import { authFetch } from "../../../api/api";
import Button from "../../../UI/Button";
import Modal from "../../../UI/Modal";
import styles from "../OrderFinances.module.css";
import {
  PROFIT_ALLOCATIONS_API,
  ACCOUNTS_API,
  CURRENCIES_API,
  PAYMENT_STATUS_OPTIONS,
} from "../constants";
import { getList, getErrorMessage, formatMoney } from "../utils";

const EMPTY_DRAFT = {
  account: "",
  amount: "",
  currency: "",
  date: "",
  payment_agent: "",
  payment_status: "PENDING",
  comment: "",
};

async function uploadDocuments(profitAllocationId, files) {
  for (const file of files) {
    const formData = new FormData();
    formData.append("file", file);

    const response = await authFetch(
      `${PROFIT_ALLOCATIONS_API}${profitAllocationId}/documents/`,
      { method: "POST", body: formData }
    );

    if (!response.ok) {
      const text = await response.text();
      throw new Error(
        text || `Не удалось загрузить "${file.name}": HTTP ${response.status}`
      );
    }
  }
}

const accountLabel = (acc) => {
  if (!acc) return "";
  const name = acc.name || acc.account_name || `Счёт #${acc.id}`;
  let code = acc.currency_code || acc.currency_code_value || "";
  if (!code && acc.currency && typeof acc.currency === "object") {
    code = acc.currency.code || acc.currency.currency_code || "";
  }
  return code ? `${name} (${code})` : name;
};

export default function CompanyAccountPaymentModal({
  orderId,
  payment = null,        // ← новое: если есть — режим редактирования
  onClose,
  onSaved,
}) {
  const isEdit = Boolean(payment?.id);

  const [accounts, setAccounts] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [draft, setDraft] = useState(() => {
    if (payment?.id) {
      return {
        account: payment.account ? String(payment.account) : "",
        amount: String(payment.amount ?? ""),
        currency: payment.currency ? String(payment.currency) : "",
        date: payment.date || "",
        payment_agent: payment.payment_agent || "",
        payment_status: payment.payment_status || "PENDING",
        comment: payment.comment || "",
      };
    }
    return { ...EMPTY_DRAFT };
  });

  const [files, setFiles] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError("");

    Promise.all([
      authFetch(ACCOUNTS_API).then((r) =>
        r.ok ? r.json() : Promise.reject(new Error(`accounts: ${r.status}`))
      ),
      authFetch(CURRENCIES_API).then((r) =>
        r.ok ? r.json() : Promise.reject(new Error(`currencies: ${r.status}`))
      ),
    ])
      .then(([accData, curData]) => {
        if (cancelled) return;
        setAccounts(getList(accData));
        setCurrencies(getList(curData));
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err.message || "Не удалось загрузить справочники.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const selectedAccount = useMemo(() => {
    if (!draft.account) return null;
    return accounts.find((a) => String(a.id) === String(draft.account)) || null;
  }, [draft.account, accounts]);

  const selectedCurrency = useMemo(() => {
    if (!draft.currency) return null;
    return currencies.find((c) => String(c.id) === String(draft.currency)) || null;
  }, [draft.currency, currencies]);

  // Автоподстановка валюты по выбранному счёту — только при создании
  useEffect(() => {
    if (isEdit) return;
    if (!selectedAccount || draft.currency) return;

    const code =
      selectedAccount.currency_code ||
      (selectedAccount.currency && typeof selectedAccount.currency === "object"
        ? selectedAccount.currency.code
        : null);

    if (!code) return;

    const match = currencies.find((c) => c.code === code);
    if (match) {
      setDraft((current) => ({ ...current, currency: String(match.id) }));
    }
  }, [selectedAccount, currencies, draft.currency, isEdit]);

  const handleChange = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const save = async (event) => {
    event?.preventDefault?.();
    setError("");

    if (!orderId) {
      setError("Не найден ID заказа.");
      return;
    }
    if (loading) {
      setError("Подожди, пока загрузятся справочники.");
      return;
    }
    if (loadError) {
      setError(loadError);
      return;
    }
    if (!draft.account) {
      setError("Выбери счёт компании.");
      return;
    }

    const amount = Number(draft.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setError("Сумма должна быть больше нуля.");
      return;
    }

    const payload = {
      type: "expense",
      allocation: null,
      order: Number(orderId),
      account: Number(draft.account),
      amount: amount.toFixed(2),
      currency: draft.currency ? Number(draft.currency) : null,
      date: draft.date || null,
      payment_agent: draft.payment_agent || "",
      comment: draft.comment || "",
      payment_status: draft.payment_status || "PENDING",
    };

    setSaving(true);
    try {
      const url = isEdit
        ? `${PROFIT_ALLOCATIONS_API}${payment.id}/`
        : PROFIT_ALLOCATIONS_API;
      const method = isEdit ? "PATCH" : "POST";

      const response = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const text = await response.text();
      let saved = null;
      if (text) {
        try { saved = JSON.parse(text); } catch { saved = null; }
      }

      if (!response.ok) {
        throw new Error(
          saved?.detail ||
            (typeof saved === "object" ? JSON.stringify(saved) : text) ||
            `Ошибка: ${response.status}`
        );
      }

      const targetId = saved?.id || payment?.id;

      if (files.length > 0 && targetId) {
        try {
          await uploadDocuments(targetId, files);
        } catch (uploadError) {
          setError(
            `Платёж сохранён, но документы не загружены: ${uploadError.message}`
          );
          onSaved?.(saved || payment);
          return;
        }
      }

      onSaved?.(saved || payment);
      onClose?.();
    } catch (err) {
      setError(err.message || "Не удалось сохранить платёж.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={() => !saving && onClose?.()}>
      <div className="my-8 w-full max-w-lg space-y-4 rounded-2xl bg-white p-6 shadow-xl">
        <h2 className="text-xl font-bold text-slate-900">
          {isEdit
            ? "Редактировать платёж со счёта компании"
            : "Платёж со счёта компании"}
        </h2>
        <p className="text-sm text-slate-500">
          {isEdit
            ? `Платёж №${payment.id} по заказу №${orderId}.`
            : `Платёж фабрике по заказу №${orderId} за счёт компании — не из клиентского распределения.`}
        </p>

        <form onSubmit={save} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Счёт компании
            </label>
            <select
              value={draft.account}
              onChange={(e) => handleChange("account", e.target.value)}
              required
              className="w-full rounded-lg border border-slate-300 px-3 py-2"
            >
              <option value="">
                {loading
                  ? "Загрузка счетов…"
                  : accounts.length === 0
                  ? "Нет доступных счетов"
                  : "Выбери счёт"}
              </option>
              {accounts.map((acc) => (
                <option key={acc.id} value={acc.id}>
                  {accountLabel(acc)}
                </option>
              ))}
            </select>
            {!loading && !loadError && accounts.length === 0 && (
              <p className="mt-1 text-xs text-red-600">
                Счета не найдены. Проверь, что в системе есть счета компании.
              </p>
            )}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Дата назначения
            </label>
            <input
              type="date"
              value={draft.date}
              onChange={(e) => handleChange("date", e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Платёж через
            </label>
            <input
              type="text"
              value={draft.payment_agent}
              onChange={(e) => handleChange("payment_agent", e.target.value)}
              placeholder="Например, банковский перевод, Alipay"
              className="w-full rounded-lg border border-slate-300 px-3 py-2"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Статус оплаты
            </label>
            <select
              value={draft.payment_status}
              onChange={(e) => handleChange("payment_status", e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2"
            >
              {PAYMENT_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Комментарий
            </label>
            <textarea
              rows={3}
              value={draft.comment}
              onChange={(e) => handleChange("comment", e.target.value)}
              placeholder="Комментарий к платежу"
              className="w-full rounded-lg border border-slate-300 px-3 py-2"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Подтверждающие документы
            </label>
            <input
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.webp"
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
              className="block w-full text-sm text-slate-700"
            />
            {files.length > 0 && (
              <ul className="mt-2 space-y-1 text-sm text-slate-600">
                {files.map((f, i) => (
                  <li key={`${f.name}-${i}`}>{f.name}</li>
                ))}
              </ul>
            )}
            {isEdit && payment?.documents?.length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-slate-500">
                  Уже прикреплённые документы
                </p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {payment.documents.map((document) => (
                    <a
                      key={document.id}
                      href={document.file_url || document.file}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded border border-slate-200 px-2 py-1 text-xs text-blue-700 hover:bg-blue-50"
                    >
                      {document.original_name || `Документ #${document.id}`} ↗
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Сумма
              </label>
              <input
                type="number"
                onWheel={(e) => e.currentTarget.blur()}
                min="0.01"
                step="0.01"
                required
                value={draft.amount}
                onChange={(e) => handleChange("amount", e.target.value)}
                placeholder="100.00"
                className={`${styles.noNumberSpinner} w-full rounded-lg border border-slate-300 px-3 py-2`}
              />
              {selectedCurrency && draft.amount && (
                <p className="mt-1 text-xs text-slate-500">
                  {selectedCurrency.symbol} {formatMoney(draft.amount)}
                </p>
              )}
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Валюта
              </label>
              <select
                value={draft.currency}
                onChange={(e) => handleChange("currency", e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2"
              >
                <option value="">—</option>
                {currencies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.symbol} {c.code}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </form>

        {error && (
          <div className="whitespace-pre-wrap break-words rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="secondary"
            disabled={saving}
            onClick={onClose}
          >
            Отмена
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={saving || loading}
            onClick={save}
          >
            {saving
              ? "Сохранение…"
              : isEdit
              ? "Сохранить изменения"
              : "Сохранить платёж"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}